<script setup>
  import {inject, nextTick, onBeforeUnmount, reactive, ref} from "vue";

  import ComponentBmpImportDialogBarcodeScannerTab from "@components/Dialogs/BmpImportDialog/Tabs/BarcodeScannerTab.vue";
  import ComponentBmpImportDialogDeviceCameraScanTab from "@components/Dialogs/BmpImportDialog/Tabs/DeviceCameraScanTab.vue";
  import ComponentBmpImportDialogUploadTab from "@components/Dialogs/BmpImportDialog/Tabs/UploadTab.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentPatientDoesNotMatch from "@components/PatientSelect/PatientDoesNotMatch.vue";
  import ComponentUniversalPatientAssignment from "@components/PatientSelect/UniversalPatientAssignment.vue";
  import ComponentTab from "@components/Tab/Tab.vue";
  import ComponentTabs from "@components/Tab/Tabs.vue";
  import {router} from "@inertiajs/vue3";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import Bmp from "@utils/Bmp.js";
  import resetState from "@utils/Helpers/ResetState.js";
  import {pdfRouter} from "@utils/pdfRouter/pdfRouter.js";

  defineOptions({name: "ComponentBmpImportDialog"});

  defineProps({
    title: {
      type: String,
      default: "Neue Medikationsanalyse mit bundeseinheitlichem Medikationsplan (BMP) erstellen",
    },
  });

  const privacy = inject("$privacy");

  const initialState = {
    status: "tabs",
    activeTab: "",
    requestedPage: null,
    error: {type: null, text: null},
    patient: null, // patient to import
    currentPatient: null, // when dialog opens within a patient detail view
    uniqueInstanceId: null,
    pagesScanned: {totalPages: 0, numbers: [], xml: []},
    parsedXml: null,
    importToCurrentPatient: false,
  };

  const refUniversalPatientAssignment = ref(null);
  const refDialog = ref(null);
  const refDialogBarcodeScanner = ref(null);
  const state = reactive({...initialState});

  onBeforeUnmount(() => {
    close();
  });

  const reset = (not = []) => {
    for (const [key, value] of Object.entries(initialState)) {
      if (!not.includes(key)) {
        state[key] = resetState(value);
      }
    }
  };

  const open = (patient = null) => {
    reset();
    state.currentPatient = patient;
    refDialog.value.open();
  };

  const close = () => {
    refDialog.value?.close();
    refUniversalPatientAssignment.value?.clearPatient();
    reset();
  };

  const tabChanged = (newActiveTab) => {
    if (state.activeTab !== newActiveTab) {
      reset(["activeTab", "currentPatient"]);
      state.activeTab = newActiveTab;
    }
    if (newActiveTab === "Barcodescanner") {
      nextTick(() => {
        refDialogBarcodeScanner.value.setFocus();
      });
    }
  };

  const existingPatientSelected = (selectedPatient) => {
    saveAndOpenRecord(selectedPatient, false);
  };

  const newPatient = (patient) => {
    saveAndOpenRecord(patient, true);
  };

  const performImportToCurrentPatient = () => {
    saveAndOpenRecord(state.currentPatient, false);
  };

  const setRequestedPage = (text) => {
    state.requestedPage = text;
  };

  const setError = (type, text) => {
    state.error.type = type;
    state.error.text = text;
  };

  const clearError = () => {
    state.error.type = null;
    state.error.text = null;
  };

  const processXml = async (xml) => {
    xml = xml.trim();

    // some scanners prepend a '#', which we have to remove
    // (yes, this is the "copy & paste" case, but it
    // should behave in the way as a scan would)
    if (xml.charAt(0) === "#") {
      xml = xml.slice(1);
    }

    // remove <?xml ...> "header" and leading line breaks (after the removal)
    xml = xml.replace(/<\?xml.*?\?>/, "").replace(/^\n+/, "");

    if (xml.length === 0) {
      return;
    }

    // From old Frontend
    if (xml.match(/\/MP>/gm)?.length > 1) {
      const xmlSplit = xml.split("</MP>");

      for (const element of xmlSplit) {
        const xmlPart = element.trim();
        if (xmlPart.length > 0) {
          await processXml(xmlPart + "</MP>");
          if (state.error.type === "error") break;
        }
      }

      return;
    }

    let currentPageNumber, scannedUniqueInstanceId;

    try {
      if (!Bmp.validateXml(xml)) throw new Error("Es wurde kein Basiselement <MP> gefunden.");

      currentPageNumber = Bmp.getCurrentPageNumber(xml);
      state.pagesScanned.totalPages = Bmp.getTotalPageNumber(xml);
      scannedUniqueInstanceId = Bmp.getUniqueInstanceId(xml);

      // patient to search for
      state.patient = {
        firstname: Bmp.getPatientFirstname(xml) || null,
        lastname: Bmp.getPatientLastname(xml) || null,
        gender: Bmp.getPatientGender(xml) || null,
        birthdate: Bmp.getPatientBirthdate(xml) || null,
        insurancenumber: Bmp.getPatientInsurancenumber(xml) || null,
      };
    } catch (error) {
      let message = "Der erfasste BMP ist ungültig";
      message += error.message ? " (" + error.message + ")." : ".";
      return setError("error", message);
    }

    clearError();

    if (state.uniqueInstanceId && state.uniqueInstanceId !== scannedUniqueInstanceId) {
      setError("error", "Bitte erfassen Sie nur Seiten des gleichen Medikationsplans.");
    } else if (state.pagesScanned.numbers.includes(currentPageNumber)) {
      setError("error", "Sie haben diese Seite bereits erfasst.");
    } else if (state.pagesScanned.numbers.length <= state.pagesScanned.totalPages) {
      // add page
      state.pagesScanned.numbers.push(currentPageNumber);
      state.pagesScanned.xml.push(xml);
      state.uniqueInstanceId = scannedUniqueInstanceId;

      if (state.pagesScanned.numbers.length < state.pagesScanned.totalPages) {
        state.status = "tabs";
        let nextPage = 0;

        if (!state.pagesScanned.numbers.includes(1)) {
          nextPage = 1;
        } else if (!state.pagesScanned.numbers.includes(2)) {
          nextPage = 2;
        } else if (!state.pagesScanned.numbers.includes(3)) {
          nextPage = 3;
        } else if (!state.pagesScanned.numbers.includes(4)) {
          nextPage = 4;
        } else if (!state.pagesScanned.numbers.includes(5)) {
          nextPage = 5;
        }

        setRequestedPage(`Bitte erfassen Sie nun Seite ${nextPage} von ${state.pagesScanned.totalPages}.`);
      } else if (state.pagesScanned.numbers.length === state.pagesScanned.totalPages) {
        continueWithCurrentPages();
      }
    } else {
      setError(
        "error",
        "Es ist ein unbekannter Fehler aufgetreten. Bitte schließen Sie den Dialog und versuchen es erneut.",
      );
    }
  };

  async function continueWithCurrentPages() {
    // all pages are scanned
    state.status = "loading";
    state.parsedXml = await parseXml(state.pagesScanned.xml);

    if (
      // state.importToCurrentPatient === false &&
      state.currentPatient &&
      (state.currentPatient.firstname !== state.patient?.firstname ||
        state.currentPatient.lastname !== state.patient?.lastname ||
        state.currentPatient.gender !== state.patient?.gender ||
        state.currentPatient.birthdate !== state.patient?.birthdate ||
        state.currentPatient.insurancenumber !== state.patient?.insurancenumber)
    ) {
      // we are within a patient detail view, but the imported bmp
      // does not match the current patient
      state.status = "patientDoesNotMatch";
    } else {
      state.status = "universalPatientAssignment";
      nextTick(async () => {
        // workaround, second tick to give vue some extra time for
        // the ref assignment due to the changed status to
        // avoid issue when accessing "setPatient"
        await nextTick();
        refUniversalPatientAssignment.value.setPatient(state.patient);
      });
    }
  }

  const parseXml = (pages) => {
    return new Promise((resolve, reject) => {
      pdfRouter.decodeXml(
        {pages: pages},
        {
          onSuccess: (response) => resolve(response.data),
          onError: (error) => reject(error),
        },
      );
    });
  };

  const handleMatch = (count) => {
    // when there is exact 1 match and a currentPatient is set,
    // (which means that the user imported into an existing
    // patient), we automatically continue the import
    if (count === 1 && state.currentPatient) {
      performImportToCurrentPatient();
    }
  };

  const saveAndOpenRecord = async (patient, isNewPatient = false) => {
    state.status = "saving";

    state.parsedXml.patient = await privacy.encryptPatient(patient);

    router.post(route("init-from-json", {origin: "bmp", patient: isNewPatient ? null : patient?.id}), state.parsedXml, {
      preserveState: false, // important to force reload and re-initialization of other components
      preserveScroll: false,
      onSuccess: () => close(),
      onError: () => {
        reset(["activeTab", "currentPatient"]);
        setError("error", "Beim Importieren ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
        state.status = "tabs";
      },
    });
  };

  function skip() {
    continueWithCurrentPages();
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refDialog" :dialog-title="title" dialog-width="large" :test-id="getTestId('bmp-import')">
    <template #content>
      <div v-if="state.status === 'tabs'">
        <ComponentTabs class="space-x-2" :active-tab="state.activeTab" @click="tabChanged">
          <ComponentTab
            v-slot="{isActive}"
            title="Barcodescanner"
            :test-id="getTestId('bmp-import-dialog-barcode-scanner')"
          >
            <ComponentBmpImportDialogBarcodeScannerTab
              v-if="isActive"
              ref="refDialogBarcodeScanner"
              :error="state.error"
              :requested-page="state.requestedPage"
              :test-id="getTestId('bmp-import-dialog')"
              @input="processXml"
              @skip="skip"
            />
          </ComponentTab>

          <ComponentTab
            v-slot="{isActive}"
            title="Gerätekamera-Scan"
            :test-id="getTestId('bmp-import-dialog-device-camera-scanner')"
          >
            <ComponentBmpImportDialogDeviceCameraScanTab
              v-if="isActive"
              :error="state.error"
              :requested-page="state.requestedPage"
              :test-id="getTestId('bmp-import-dialog')"
              @error="setError"
              @input="processXml"
              @skip="skip"
            />
          </ComponentTab>

          <ComponentTab v-slot="{isActive}" title="Hochladen" :test-id="getTestId('bmp-import-dialog-xml-upload')">
            <ComponentBmpImportDialogUploadTab
              v-if="isActive"
              :error="state.error"
              :requested-page="state.requestedPage"
              :test-id="getTestId('bmp-import-dialog')"
              @error="setError"
              @input="processXml"
              @skip="skip"
            />
          </ComponentTab>
        </ComponentTabs>
      </div>

      <div v-else-if="state.status === 'patientDoesNotMatch'">
        <ComponentPatientDoesNotMatch :current-patient="state.currentPatient" :patient="state.patient" />
      </div>

      <div v-else-if="state.status === 'universalPatientAssignment'">
        <ComponentUniversalPatientAssignment
          ref="refUniversalPatientAssignment"
          @existing-patient-selected="(patient) => existingPatientSelected(patient)"
          @new-patient="(patient) => newPatient(patient)"
          @has-match="handleMatch"
        />
      </div>

      <div v-else-if="state.status === 'saving'" class="flex flex-col items-center justify-center py-10">
        <div class="py-4 text-lg">Wird gespeichert...</div>
        <MedSpinner size="2xl" :data-test="getTestId('bmp-import-dialog-saving-spinner')" />
      </div>

      <div v-else class="flex flex-col items-center justify-center py-10">
        <MedSpinner size="2xl" :data-test="getTestId('bmp-import-dialog-loading-spinner')" />
      </div>
    </template>

    <template #actions>
      <template v-if="state.status === 'patientDoesNotMatch'">
        <MedButton
          variant="primary"
          :data-test="getTestId('bmp-import-dialog-perform-import-to-current-patient-button')"
          @click="performImportToCurrentPatient"
        >
          In bestehenden Patienten importieren
        </MedButton>
      </template>

      <!-- <Teleport> target of UniversalPatientAssignment component (reverts space-x margin when empty) -->
      <div id="UniversalPatientAssignmentSearchButton" />

      <MedButton :data-test="getTestId('bmp-import-dialog-close-button')" @click="close">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
